/**
 * SpecialCasesDataTable
 *
 * Renders the special cases DataGrid (MUI) with filters, cell renderers,
 * and action buttons. This is "case 2" from the original CollabsView renderBody switch.
 */
import { useState, useMemo } from "react";
import { DataGrid, QuickFilter, QuickFilterControl } from "@mui/x-data-grid";
import Langs, { Localize } from "../../app/lang/langs";
import ImageButton from "../../components/buttons/ImageButton";
import userInfo from "../../assets/images/userInfo.png";
import userInfoFocus from "../../assets/images/userInfoFocus.png";
import styles from "./CollabsView.module.css";
import {
  renderDateCell,
  renderSpecialCaseCell,
  renderCreatedAtCellSC,
  renderUpdatedAtCellSC,
  usePinnedActionsColumn,
} from "./collabsTableUtils";

/**
 * @param {Object} props
 * @param {Object} props.apiRef - MUI useGridApiRef() ref
 * @param {Array} props.data - The special cases list to display
 * @param {string} props.scrollHeight - CSS scroll height for the table
 * @param {Function} props.onViewDetails - Called when the "view details" button is clicked
 */
export default function SpecialCasesDataTable({
  apiRef,
  data,
  scrollHeight,
  onViewDetails,
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  usePinnedActionsColumn(apiRef);

  const columns = useMemo(() => [
    { field: "id", headerName: Localize("ID"), minWidth: 128, sortable: true },
    { field: "nom_collab", headerName: Localize("Family Name"), minWidth: 128, sortable: true, filterable: true },
    { field: "prenom_collab", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "case", headerName: Localize("category"), minWidth: 176, sortable: true, filterable: true,
      renderCell: renderSpecialCaseCell,
    },
    { field: "team_name", headerName: Localize("Client"), minWidth: 160, sortable: true, filterable: true },
    {
      field: "startDate", headerName: Localize("START_DATE"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "endDate", headerName: Localize("END_DATE"), minWidth: 160,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: renderDateCell,
    },
    {
      field: "created_at", headerName: Localize("created_at"), minWidth: 200, sortable: true,
      renderCell: renderCreatedAtCellSC,
    },
    {
      field: "updated_at", headerName: Localize("updated_at"), minWidth: 200, sortable: true,
      renderCell: renderUpdatedAtCellSC,
    },
    { field: "comments", headerName: Localize("Comments"), minWidth: 160, sortable: true, filterable: true },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      disableColumnMenu: true, disableReorder: true,
      renderCell: (params) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <ImageButton
            image={userInfo}
            imageFocus={userInfoFocus}
            onClick={() => onViewDetails(params.row)}
            active={true}
            tooltip={<Langs str="VIEW_DETAILS" />}
            width={32}
          />
        </div>
      ),
    },
  ], [onViewDetails]);

  const CustomToolbar = () => (
    <div className="p-2">
      <QuickFilter debounceMs={300}>
        <QuickFilterControl />
      </QuickFilter>
    </div>
  );

  return (
    <div className={styles.collabsCard}>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          apiRef={apiRef}
          rows={data || []}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          slots={{ toolbar: CustomToolbar }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", py: 1 },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              backgroundColor: "var(--surface-card, #fff)",
            },
          }}
        />
      </div>
    </div>
  );
}
